import { useCallback, useEffect, useRef } from 'react';
import { useDrag, useDragLayer, useDrop } from 'react-dnd';

interface DragItem {
  type: string;
  index: number;
}

export interface DraggableSearchHandler {
  ref: (node: HTMLDivElement | null) => void;
  isDragging: boolean;
}

/**
 * Hook to create drag-and-drop functionality for a single list item.
 * Primarily used for ItemFinder search entries, but reusable for any
 * drag-and-drop list reordering.
 *
 * @param index - Index of the item in the list
 * @param onSearchMove - Callback when an item is moved
 * @param dragType - The drag type identifier used for react-dnd
 * @param canDrag - Optional function to determine if the item can be dragged
 */
export const useDraggableSearch = (
  index: number,
  onSearchMove: (dragIndex: number, hoverIndex: number) => void,
  dragType: string,
  canDrag?: () => boolean,
): DraggableSearchHandler => {
  const dropRef = useRef<HTMLDivElement>(null);
  const lastScrollAtRef = useRef(0);
  const scrollVelocityRef = useRef(0);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  /**
   * Returns the nearest vertically scrollable ancestor of a node.
   * Checks parent elements for overflowY ('auto', 'scroll', 'overlay')
   * and scrollHeight > clientHeight. Defaults to document.scrollingElement.
   * Useful for detecting which container will scroll during drag operations.
   */
  const getScrollableAncestor = (node: HTMLElement | null) => {
    if (!node || typeof window === 'undefined') {
      return null;
    }

    let current: HTMLElement | null = node.parentElement;
    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      // Check if the element is actually scrollable by the user.
      // Only consider it scrollable if overflowY allows scrolling
      // ('auto', 'scroll', or 'overlay') and content exceeds container height.
      // This ensures auto-scroll works correctly during drag-and-drop.
      const isScrollable =
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        current.scrollHeight > current.clientHeight;

      if (isScrollable) {
        return current;
      }
      current = current.parentElement;
    }

    return document.scrollingElement as HTMLElement | null;
  };

  /**
   * Auto-scrolls the nearest scrollable container (or window) when the pointer is near the top or bottom edge.
   * Calculates distance to edges and sets scroll velocity proportional to proximity. Updates scroll only if enough
   * time has passed or velocity changed. Resets velocity to 0 when pointer is away from edges.
   */
  const scrollIfNeeded = (pointerPosition: { x: number; y: number }) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!scrollContainerRef.current) {
      scrollContainerRef.current = getScrollableAncestor(dropRef.current);
    }

    const scrollContainer = scrollContainerRef.current;
    const isWindowScroll =
      !scrollContainer ||
      scrollContainer === document.body ||
      scrollContainer === document.documentElement ||
      scrollContainer === document.scrollingElement;
    const scrollRect = scrollContainer ? scrollContainer.getBoundingClientRect() : null;
    const now = Date.now();
    const viewportHeight = scrollRect ? scrollRect.height : window.innerHeight;
    const edgeThreshold = Math.max(24, Math.min(80, viewportHeight * 0.15));
    const distanceToTop = scrollRect ? pointerPosition.y - scrollRect.top : pointerPosition.y;
    const distanceToBottom = scrollRect ? scrollRect.bottom - pointerPosition.y : viewportHeight - pointerPosition.y;
    let velocity = 0;

    if (distanceToTop < edgeThreshold) {
      velocity = -Math.ceil((edgeThreshold - distanceToTop) / 6);
    } else if (distanceToBottom < edgeThreshold) {
      velocity = Math.ceil((edgeThreshold - distanceToBottom) / 6);
    }

    if (velocity !== 0 && (now - lastScrollAtRef.current > 16 || velocity !== scrollVelocityRef.current)) {
      if (isWindowScroll) {
        window.scrollBy(0, velocity);
      } else if (scrollContainer) {
        scrollContainer.scrollBy(0, velocity);
      }
      lastScrollAtRef.current = now;
      scrollVelocityRef.current = velocity;
    } else if (velocity === 0) {
      scrollVelocityRef.current = 0;
    }
  };

  const [, drop] = useDrop<DragItem, unknown, unknown>({
    accept: dragType,
    hover(item, monitor) {
      if (!dropRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const pointerPosition = monitor.getClientOffset();
      if (!pointerPosition) {
        return;
      }

      scrollIfNeeded(pointerPosition);

      const hoverClientY = pointerPosition.y - hoverBoundingRect.top;

      // Simple middle-point logic to prevent excessive updates
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      onSearchMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag(
    {
      type: dragType,
      item: () => ({ index }),
      canDrag,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    },
    [index, canDrag],
  );

  const { isDragging: isLayerDragging, clientOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    clientOffset: monitor.getClientOffset(),
  }));

  useEffect(() => {
    if (!isLayerDragging || !clientOffset) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      scrollIfNeeded(clientOffset);
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [clientOffset, isLayerDragging, scrollIfNeeded]);

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      dropRef.current = node;
      scrollContainerRef.current = null;
      if (node) {
        drag(drop(node));
      }
    },
    [drag, drop],
  );

  return { ref: setRef, isDragging };
};
